/**
 * Catalog of available rollup parameters with descriptions, defaults, and use case examples
 */

export interface RollupParameterOption {
  key: string;
  label: string;
  description: string;
  type: 'boolean' | 'string' | 'number' | 'array';
  options?: Array<{ value: string; label: string; description?: string }>;  // For dropdowns
  placeholder?: string;
  tooltip: string;
  useCases: string[];  // e.g., ['revenue', 'expenses', 'supply']
  required?: boolean;
  dependsOn?: string;  // Show only if another param is set
}

export const ROLLUP_PARAMETER_CATALOG: Record<string, RollupParameterOption> = {
  aggregation_enabled: {
    key: 'aggregation_enabled',
    label: 'Enable Aggregation',
    description: 'Enable rollup of multiple source items into this transition',
    type: 'boolean',
    tooltip: 'When enabled, this transition will aggregate data from source artifact items',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: true,
  },
  
  aggregation_type: {
    key: 'aggregation_type',
    label: 'Aggregation Type',
    description: 'How to group source items',
    type: 'string',
    options: [
      { 
        value: 'time_based', 
        label: 'Time-Based', 
        description: 'Group by date/time period (e.g., monthly revenue lane)' 
      },
      { 
        value: 'parent_based', 
        label: 'Parent-Based', 
        description: 'Group by parent artifact (e.g., invoice line items)' 
      },
    ],
    tooltip: 'Determines whether to aggregate by time periods or by parent artifact relationship',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: true,
    dependsOn: 'aggregation_enabled',
  },
  
  parent_artifact_ref_field: {
    key: 'parent_artifact_ref_field',
    label: 'Parent Reference Field',
    description: 'Field in child items linking to parent artifact',
    type: 'string',
    placeholder: 'e.g., invoice_id, parent_order_id',
    tooltip: 'Name of the field in child artifacts that contains the parent artifact ID',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'aggregation_type',
  },
  
  parent_artifact_type: {
    key: 'parent_artifact_type',
    label: 'Parent Artifact Type',
    description: 'Type of parent artifact',
    type: 'string',
    placeholder: 'e.g., invoice, purchase_order',
    tooltip: 'Artifact type of the parent (for validation and queries)',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'aggregation_type',
  },
  
  aggregation_period: {
    key: 'aggregation_period',
    label: 'Aggregation Period',
    description: 'Time period for grouping source items',
    type: 'string',
    options: [
      { value: 'daily', label: 'Daily', description: 'Roll up by day' },
      { value: 'weekly', label: 'Weekly', description: 'Roll up by week' },
      { value: 'monthly', label: 'Monthly', description: 'Roll up by month' },
      { value: 'quarterly', label: 'Quarterly', description: 'Roll up by quarter' },
      { value: 'yearly', label: 'Yearly', description: 'Roll up by year' },
    ],
    tooltip: 'How often to create aggregate lane items',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: true,
    dependsOn: 'aggregation_enabled',
  },
  
  aggregation_timezone: {
    key: 'aggregation_timezone',
    label: 'Timezone',
    description: 'Timezone for aggregation period boundaries',
    type: 'string',
    placeholder: 'e.g., America/Denver, UTC',
    tooltip: 'Timezone used for determining period boundaries (affects month/day start times)',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'aggregation_enabled',
  },
  
  source_artifact_type: {
    key: 'source_artifact_type',
    label: 'Source Artifact Type',
    description: 'Type of artifact to aggregate from',
    type: 'string',
    placeholder: 'e.g., revenue, expense, supply_item',
    tooltip: 'The artifact type containing the items to be rolled up',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: true,
    dependsOn: 'aggregation_enabled',
  },
  
  source_state_filter: {
    key: 'source_state_filter',
    label: 'Source State Filter',
    description: 'Filter source items by state (comma-separated, or leave empty for all states)',
    type: 'string',
    placeholder: 'e.g., recognized, earned (or leave blank for all)',
    tooltip: 'Only aggregate items in these states. Leave empty to aggregate all states.',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'aggregation_enabled',
  },
  
  selector_field: {
    key: 'selector_field',
    label: 'Selector Field',
    description: 'Field in source items to match against bucket',
    type: 'string',
    placeholder: 'e.g., recognized_bucket_id, booked_bucket_id',
    tooltip: 'The field that contains the bucket key in source items',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: true,
    dependsOn: 'aggregation_enabled',
  },
  
  selector_comparison: {
    key: 'selector_comparison',
    label: 'Selector Comparison Type',
    description: 'How to match selector field against bucket',
    type: 'string',
    options: [
      { value: 'equals', label: 'Equals', description: 'Exact match' },
      { value: 'in', label: 'In', description: 'Value is in a list' },
      { value: 'between', label: 'Between', description: 'Value is in a range' },
    ],
    tooltip: 'Comparison operator for matching source items to bucket',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'selector_field',
  },
  
  selector_value: {
    key: 'selector_value',
    label: 'Selector Value Expression',
    description: 'Value or expression to match against',
    type: 'string',
    placeholder: 'e.g., $.bucket_id, $.period_key',
    tooltip: 'Expression referencing the lane item field to match ($ prefix = current item)',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'selector_field',
  },
  
  measure_total_field: {
    key: 'measure_total_field',
    label: 'Measure Field (Total)',
    description: 'Field to calculate total from',
    type: 'string',
    placeholder: 'e.g., amount_recognized_conv, quantity',
    tooltip: 'The numeric field to sum/aggregate',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: true,
    dependsOn: 'aggregation_enabled',
  },
  
  measure_total_operation: {
    key: 'measure_total_operation',
    label: 'Measure Operation',
    description: 'How to aggregate the measure field',
    type: 'string',
    options: [
      { value: 'sum', label: 'Sum', description: 'Add all values' },
      { value: 'avg', label: 'Average', description: 'Calculate mean' },
      { value: 'min', label: 'Minimum', description: 'Find lowest value' },
      { value: 'max', label: 'Maximum', description: 'Find highest value' },
      { value: 'count', label: 'Count', description: 'Count records' },
    ],
    tooltip: 'Mathematical operation to apply',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: true,
    dependsOn: 'measure_total_field',
  },
  
  measure_count_enabled: {
    key: 'measure_count_enabled',
    label: 'Enable Count Measure',
    description: 'Count number of source items',
    type: 'boolean',
    tooltip: 'When enabled, counts the number of source records aggregated',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'aggregation_enabled',
  },
  
  measure_conditional_field: {
    key: 'measure_conditional_field',
    label: 'Conditional Measure Field',
    description: 'Field for conditional aggregation (e.g., refunds)',
    type: 'string',
    placeholder: 'e.g., amount_recognized_conv',
    tooltip: 'Field to aggregate conditionally based on filter',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'aggregation_enabled',
  },
  
  measure_conditional_operation: {
    key: 'measure_conditional_operation',
    label: 'Conditional Operation',
    description: 'Operation for conditional measure',
    type: 'string',
    options: [
      { value: 'sum_if', label: 'Sum If', description: 'Sum values matching filter' },
      { value: 'count_if', label: 'Count If', description: 'Count records matching filter' },
    ],
    tooltip: 'Mathematical operation with filter condition',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'measure_conditional_field',
  },
  
  measure_conditional_filter: {
    key: 'measure_conditional_filter',
    label: 'Conditional Filter',
    description: 'Filter condition for conditional measure',
    type: 'string',
    placeholder: 'e.g., type == "refund", status == "reversed"',
    tooltip: 'Boolean expression to filter records for conditional aggregation',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'measure_conditional_field',
  },
  
  bucket_key_format: {
    key: 'bucket_key_format',
    label: 'Bucket Key Format',
    description: 'Date format for bucket keys',
    type: 'string',
    placeholder: 'e.g., YYYY-MM, YYYY-Www, YYYY-QQ',
    tooltip: 'Format string for generating bucket identifiers (YYYY-MM for monthly)',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'aggregation_enabled',
  },
  
  bucket_key_field: {
    key: 'bucket_key_field',
    label: 'Bucket Key Field',
    description: 'Field name where bucket key is stored',
    type: 'string',
    placeholder: 'e.g., bucket_id, period_key',
    tooltip: 'Name of the field in the lane item that stores the bucket identifier',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'bucket_key_format',
  },
  
  idempotency_enabled: {
    key: 'idempotency_enabled',
    label: 'Enable Idempotency',
    description: 'Ensure each source event is processed only once',
    type: 'boolean',
    tooltip: 'Prevents duplicate processing of the same source records',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
  },
  
  idempotency_event_id_field: {
    key: 'idempotency_event_id_field',
    label: 'Event ID Field',
    description: 'Field containing unique event identifier',
    type: 'string',
    placeholder: 'e.g., recognition_event_id, transaction_id',
    tooltip: 'Field in source items containing unique event ID for idempotency tracking',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'idempotency_enabled',
  },
  
  idempotency_watermark_field: {
    key: 'idempotency_watermark_field',
    label: 'Watermark Field',
    description: 'Field storing last processed event sequence',
    type: 'string',
    placeholder: 'e.g., last_processed_event_seq',
    tooltip: 'Field in lane item tracking the highest event sequence processed',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'idempotency_enabled',
  },
  
  window_start_field: {
    key: 'window_start_field',
    label: 'Window Start Field',
    description: 'Field containing period start timestamp',
    type: 'string',
    placeholder: 'e.g., bucket_start, period_start',
    tooltip: 'Field in lane item defining the start of the aggregation window',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'aggregation_enabled',
  },
  
  window_end_field: {
    key: 'window_end_field',
    label: 'Window End Field',
    description: 'Field containing period end timestamp',
    type: 'string',
    placeholder: 'e.g., bucket_end, period_end',
    tooltip: 'Field in lane item defining the end of the aggregation window',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'aggregation_enabled',
  },
  
  window_include_boundaries: {
    key: 'window_include_boundaries',
    label: 'Include Window Boundaries',
    description: 'Include items exactly at window start/end timestamps',
    type: 'boolean',
    tooltip: 'When true, includes items with timestamps exactly matching window boundaries',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'window_start_field',
  },
  
  target_field_source: {
    key: 'target_field_source',
    label: 'Target Field Source',
    description: 'Configuration field containing target value',
    type: 'string',
    placeholder: 'e.g., config.target_recognized_monthly',
    tooltip: 'Path to configuration field with target value for comparison',
    useCases: ['revenue', 'expenses', 'supply', 'demand'],
    required: false,
  },
  
  target_comparison_enabled: {
    key: 'target_comparison_enabled',
    label: 'Enable Target Comparison',
    description: 'Compare aggregated value against target',
    type: 'boolean',
    tooltip: 'When enabled, compares the rollup total to the target value',
    useCases: ['revenue', 'expenses', 'supply', 'demand'],
    required: false,
    dependsOn: 'target_field_source',
  },
  
  multi_state_input_enabled: {
    key: 'multi_state_input_enabled',
    label: 'Multi-State Input',
    description: 'Allow reading items in any state (not just one specific state)',
    type: 'boolean',
    tooltip: 'When enabled for MATERIAL transitions, can aggregate items regardless of their state',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
  },
  
  preserve_source_state: {
    key: 'preserve_source_state',
    label: 'Preserve Source State',
    description: 'Do not change the state of source items during aggregation',
    type: 'boolean',
    tooltip: 'When enabled, source items are read-only (no state transitions applied)',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'multi_state_input_enabled',
  },
};

// Artifact Generation Parameter Catalog (for CREATION transitions)
export const ARTIFACT_GENERATION_CATALOG: Record<string, RollupParameterOption> = {
  artifact_generation_mode: {
    key: 'artifact_generation_mode',
    label: 'Generation Mode',
    description: 'How many artifact items can be created/exist concurrently',
    type: 'string',
    options: [
      { value: 'singleton', label: 'Singleton', description: 'Only one item at a time' },
      { value: 'sequential', label: 'Sequential', description: 'One at a time, sequential' },
      { value: 'concurrent', label: 'Concurrent', description: 'Many items simultaneously' },
    ],
    tooltip: 'Controls concurrent artifact creation',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
  },
  
  replacement_behavior: {
    key: 'replacement_behavior',
    label: 'Replacement Behavior',
    description: 'What to do if an artifact already exists (singleton mode)',
    type: 'string',
    options: [
      { value: 'archive_previous', label: 'Archive Previous', description: 'Archive the existing item' },
      { value: 'delete_previous', label: 'Delete Previous', description: 'Delete the existing item' },
      { value: 'error_if_exists', label: 'Error If Exists', description: 'Throw error if item exists' },
    ],
    tooltip: 'How to handle existing artifacts in singleton mode',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'artifact_generation_mode',
  },
  
  completion_required: {
    key: 'completion_required',
    label: 'Completion Required',
    description: 'Must previous item complete before creating next (sequential mode)',
    type: 'boolean',
    tooltip: 'Enforces strict sequential order',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'artifact_generation_mode',
  },
  
  max_active_items: {
    key: 'max_active_items',
    label: 'Max Active Items',
    description: 'Maximum simultaneously active items (sequential mode)',
    type: 'number',
    placeholder: 'e.g., 1 for strict sequential',
    tooltip: 'Limits number of active items',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'artifact_generation_mode',
  },
  
  max_total_items: {
    key: 'max_total_items',
    label: 'Max Total Items',
    description: 'Optional limit on total items (concurrent mode)',
    type: 'number',
    placeholder: 'Leave empty for unlimited',
    tooltip: 'Optional cap on total number of items',
    useCases: ['revenue', 'expenses', 'supply', 'demand', 'delivery'],
    required: false,
    dependsOn: 'artifact_generation_mode',
  },
};

// Helper to get parameters for a specific use case
export function getRollupParametersForUseCase(useCase: string): RollupParameterOption[] {
  return Object.values(ROLLUP_PARAMETER_CATALOG).filter(param => 
    param.useCases.includes(useCase)
  );
}

// Validation schemas for common patterns
export const ROLLUP_VALIDATION_SCHEMAS = {
  revenue_recognized: [
    'aggregation_enabled',
    'aggregation_period',
    'source_artifact_type',
    'selector_field',
    'measure_total_field',
    'measure_total_operation',
    'bucket_key_format',
  ],
  
  revenue_bookings: [
    'aggregation_enabled',
    'aggregation_period',
    'source_artifact_type',
    'selector_field',
    'measure_total_field',
    'measure_total_operation',
  ],
  
  supply_inventory: [
    'aggregation_enabled',
    'aggregation_period',
    'source_artifact_type',
    'selector_field',
    'measure_total_field',
    'measure_total_operation',
    'measure_count_enabled',
  ],
};

